import { useEffect, useState, type MouseEvent } from "react";
import { navLinks } from "@/mocks/home";

export default function TopBar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleAnchor = (event: MouseEvent<HTMLAnchorElement>, hash: string) => {
    event.preventDefault();
    setMenuOpen(false);
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const darkText = scrolled || menuOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        darkText ? "border-b border-background-200 bg-background-50/95 backdrop-blur" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-10">
        <a
          href="#top"
          onClick={(event) => handleAnchor(event, "#top")}
          className="flex cursor-pointer items-center gap-2.5"
          aria-label="灵灵黑料首页"
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md font-heading text-lg font-bold transition-colors md:h-10 md:w-10 ${
              darkText ? "bg-primary-500 text-background-50" : "bg-background-50/95 text-primary-600"
            }`}
          >
            灵
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-wide transition-colors md:text-xl ${
                darkText ? "text-foreground-950" : "text-background-50"
              }`}
            >
              灵灵黑料
            </span>
            <span
              className={`mt-1 font-label text-[10px] tracking-[0.22em] transition-colors ${
                darkText ? "text-foreground-500" : "text-background-50/70"
              }`}
            >
              事件汇总 · 时间线档案
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(event) => handleAnchor(event, link.href)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 font-label text-sm transition-colors ${
                darkText
                  ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                  : "text-background-50/85 hover:bg-background-50/15 hover:text-background-50"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a
            href="#subscribe"
            onClick={(event) => handleAnchor(event, "#subscribe")}
            className={`cursor-pointer whitespace-nowrap rounded-md border px-4 py-2 font-label text-sm transition-colors ${
              darkText
                ? "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                : "border-background-50/40 text-background-50 hover:bg-background-50/10"
            }`}
          >
            更新提醒
          </a>
          <a
            href="#events"
            onClick={(event) => handleAnchor(event, "#events")}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            查看大合集
          </a>
        </div>

        <button
          type="button"
          aria-label="打开导航菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-md transition-colors md:hidden ${
            darkText ? "text-foreground-900 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
          }`}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 md:hidden">
          <nav className="flex flex-col">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(event) => handleAnchor(event, link.href)}
                className="flex cursor-pointer items-center justify-between border-b border-background-200 py-3 font-label text-sm text-foreground-800"
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </a>
            ))}
          </nav>
          <div className="mt-4 flex flex-col gap-2.5">
            <a
              href="#subscribe"
              onClick={(event) => handleAnchor(event, "#subscribe")}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-center font-label text-sm text-foreground-700"
            >
              更新提醒
            </a>
            <a
              href="#events"
              onClick={(event) => handleAnchor(event, "#events")}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center font-label text-sm font-medium text-background-50"
            >
              查看大合集
            </a>
          </div>
        </div>
      )}
    </header>
  );
}